import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Receipt } from "lucide-react";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { CarregandoBloco, CarregandoFormulario } from "@/components/carregando";

import { ROTA_DESPESAS } from "./rotas";
import ResumoDespesa from "./ResumoDespesa";
import SecaoAvancado from "./SecaoAvancado";
import SecaoDados from "./SecaoDados";
import useNovaDespesa from "./useNovaDespesa";

const temAvancado = (f) => f.isRecurring || parseInt(f.installments, 10) > 0 || f.modulos.length > 0 || !!f.notes;

function Rodape({ d, edicao, aoCancelar, aoSalvar }) {
  return (
    <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-3" data-e2e="rodape-despesa">
      <div className="max-w-[1400px] mx-auto flex items-center justify-end gap-3">
        <button type="button" onClick={aoCancelar} data-e2e="cancelar-despesa"
          className="h-10 px-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-500 text-sm font-medium hover:bg-red-500/20 transition">
          Cancelar
        </button>
        <button type="button" onClick={aoSalvar} disabled={d.salvando || d.carregando} data-e2e="salvar-despesa"
          className="h-12 px-7 rounded-lg bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50">
          {d.salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          {d.salvando ? "Salvando…" : edicao ? "Salvar alterações" : "Criar despesa"}
        </button>
      </div>
    </div>
  );
}

// Financeiro → Despesas → Nova Despesa / editar (tela inteira desde 21/09/2026;
// antes era um popup). Mesmo molde da Nova Compra: rodapé preso embaixo.
export default function NovaDespesaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const d = useNovaDespesa(id);
  const [avancado, setAvancado] = useState(false);
  const [sair, setSair] = useState(false);
  const edicao = !!id;

  // Ao abrir uma despesa que já usa o Avançado, ele vem aberto (como no popup).
  useEffect(() => { if (!d.carregando && temAvancado(d.form)) setAvancado(true); }, [d.carregando]); // eslint-disable-line react-hooks/exhaustive-deps

  // Volta para o mês que estava aberto na lista, não para o mês atual.
  const voltar = () => navigate(ROTA_DESPESAS, { state: state?.mes != null ? { mes: state.mes, ano: state.ano } : undefined });
  const cancelar = () => (d.alterado ? setSair(true) : voltar());
  const salvar = async () => { if (await d.salvar()) voltar(); };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-e2e="nova-despesa">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={cancelar} aria-label="Voltar para Despesas"
              className="h-10 w-10 rounded-lg border flex items-center justify-center hover:bg-muted shrink-0"><ArrowLeft className="w-4 h-4" /></button>
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-primary" /></div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">{edicao ? "Editar despesa" : "Nova despesa"}</h1>
              <p className="text-sm text-muted-foreground">Preencha os dados e, se quiser, atribua a despesa aos módulos em Avançado.</p>
            </div>
          </div>
          {/* Carregamento com cards (25/10/2026): o formulário e o resumo piscando no lugar do spinner */}
          {d.carregando ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
              <CarregandoFormulario campos={7} />
              <CarregandoBloco altura="h-72" />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
              <div className="space-y-4 min-w-0">
                <SecaoDados d={d} />
                <SecaoAvancado d={d} aberto={avancado} setAberto={setAvancado} />
              </div>
              <ResumoDespesa form={d.form} edicao={edicao} />
            </div>
          )}
        </div>
      </div>
      <Rodape d={d} edicao={edicao} aoCancelar={cancelar} aoSalvar={salvar} />

      <AlertDialog open={sair} onOpenChange={setSair}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{edicao ? "Descartar as alterações?" : "Descartar esta despesa?"}</AlertDialogTitle>
            <AlertDialogDescription>{edicao ? "A despesa continua como estava antes de você abrir." : "O que você preencheu será perdido."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={voltar}>Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
